/**
 * PWA registration with safety guards for Lovable preview/iframe contexts
 * AND for Capacitor native builds.
 *
 * Service workers must NEVER activate inside:
 *   - the Lovable editor preview (intercepts iframe navigation, serves stale
 *     responses), or
 *   - a Capacitor native WebView (the app is loaded from `capacitor://localhost`
 *     where SW scope/scheme handling is unreliable; an active SW from a
 *     prior install can serve a stale/empty response on cold start, which
 *     manifests as a *blank screen* on Android/iOS).
 *
 * In each of those contexts we both skip registration AND proactively
 * unregister any service workers that may have been installed by an earlier
 * build, so previously cached versions don't keep serving stale content.
 */

const isInIframe = (() => {
  try {
    return window.self !== window.top;
  } catch {
    // Cross-origin access throws — assume we're framed.
    return true;
  }
})();

const isPreviewHost =
  typeof window !== "undefined" &&
  (window.location.hostname.includes("id-preview--") ||
    window.location.hostname.includes("lovableproject.com"));

/**
 * Detect a Capacitor native WebView without statically importing
 * `@capacitor/core` (which would pull the plugin runtime into the critical
 * boot path). We check the global Capacitor bridge that the native shell
 * injects before any JS runs, plus the `capacitor:` / `ionic:` URL schemes
 * used by the WebView on Android and iOS.
 */
const isCapacitorNative = (() => {
  if (typeof window === "undefined") return false;
  const w = window as Window & {
    Capacitor?: { isNativePlatform?: () => boolean; getPlatform?: () => string };
  };
  if (w.Capacitor?.isNativePlatform?.()) return true;
  const proto = window.location.protocol;
  if (proto === "capacitor:" || proto === "ionic:") return true;
  return false;
})();

export async function registerPWA(): Promise<void> {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) {
    return;
  }

  // Track PWA installation regardless of whether SW registration proceeds.
  // Capacitor native never fires `appinstalled`, so this is web-only in practice.
  window.addEventListener("appinstalled", () => {
    void import("@/services/analyticsService").then(({ trackEvent }) => {
      trackEvent("pwa_installed", {
        display_mode: window.matchMedia("(display-mode: standalone)").matches
          ? "standalone"
          : "browser",
      });
    });
  });

  if (isCapacitorNative || isPreviewHost || isInIframe) {
    // Clean up any service workers that may have been registered previously
    // (e.g. by an older build that didn't have this guard, or by visiting
    // the web PWA before installing the native app on the same device).
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((r) => r.unregister()));
    } catch {
      // Ignore — best effort cleanup.
    }
    return;
  }

  try {
    const [{ registerSW }, { toast }, { trackEvent }, { default: i18n }] = await Promise.all([
      import("virtual:pwa-register"),
      import("sonner"),
      import("@/services/analyticsService"),
      import("@/i18n"),
    ]);

    const updateSW = registerSW({
      immediate: true,
      onNeedRefresh() {
        trackEvent("pwa_update_available");
        toast(i18n.t("pwa.updateAvailable"), {
          description: i18n.t("pwa.updateDescription"),
          duration: Infinity,
          action: {
            label: i18n.t("pwa.refresh"),
            onClick: () => {
              trackEvent("pwa_update_accepted");
              updateSW(true);
            },
          },
        });
      },
      onOfflineReady() {
        toast.success(i18n.t("pwa.readyOffline"), {
          description: i18n.t("pwa.readyOfflineDescription"),
        });
      },
    });
  } catch (err) {
    console.warn("PWA registration failed", err);
  }
}
